import { MutationTree } from "vuex";
import {
	CameraReferenceShot,
	DeviceChanges,
	DeviceConfiguration,
	DeviceConfigurationErrors,
	DeviceDetails,
	DeviceRecordsRetrievalStatus,
	DevicesState,
	DeviceTypeCounts,
	DeviceTypeIdentifier,
	ServerDetails,
	ServerLineProfile,
	ServerType
} from "./types";
import { getDefaultState } from "./state";
import Vue from "vue";
import { state } from "../event-category-setup";

export const mutations: MutationTree<DevicesState> = {
	setCancelTokenSource(state: DevicesState, cancelTokenSource): void {
		state.cancelTokenSource = cancelTokenSource;
	},
	setDoors(state: DevicesState, doors: DeviceDetails[]): void {
		state.doors = doors;
	},
	setGettingConfig(state: DevicesState, gettingConfig: boolean) {
		state.gettingConfig = gettingConfig;
	},
	setServersList(state: DevicesState, serversList: ServerDetails[]): void {
		state.serversList = serversList;
	},
	setServerConfig(state: DevicesState, serverConfig: DeviceConfiguration[]): void {
		state.serverConfig = serverConfig;
	},
	setServerDeviceChanges(state: DevicesState, deviceChanges: DeviceChanges): void {
		state.serverDeviceChanges = deviceChanges;
		state.serverDeviceChanges.serverId = state.currentServer.serverID;
	},
	setSettingConfig(state: DevicesState, settingConfig: boolean) {
		state.settingConfig = settingConfig;
	},
	addServersNotAlreadyInStore(state: DevicesState, servers: ServerDetails[]): void {
		servers = servers.filter(
			newServer => !state.serversList.some(existingServer => existingServer.serverID === newServer.serverID)
		);

		state.serversList.push(...servers);
	},
	updateRecordsRetrievalStatus(state: DevicesState, recordsRetrievalStatus: DeviceRecordsRetrievalStatus): void {
		state.recordsRetrievalStatus = recordsRetrievalStatus;
	},
	setFilteredServersList(state: DevicesState, data: ServerDetails[]): void {
		state.filteredServersList = data;
	},
	setExtraValue(state: DevicesState, data: string): void {
		state.currentServer.extraValue = data;
	},
	setCurrentServer(state: DevicesState, server: ServerDetails): void {
		state.currentServer = server;
	},
	setDeviceChangeErrors(state: DevicesState, errors: DeviceChanges) {
		state.deviceChangeErrors = errors;
	},
	setDeviceConfigErrors(state: DevicesState, errors: DeviceConfigurationErrors) {
		state.deviceConfigurationErrors = errors;
	},
	setServerModalVisible(state: DevicesState, isVisible: boolean) {
		state.serverModalOpened = isVisible;
	},
	updateCurrentServerRecordsRetrievalStatus(
		state: DevicesState,
		recordsRetrievalStatus: DeviceRecordsRetrievalStatus
	): void {
		Vue.set(state.currentServer, "recordsRetrievalStatus", recordsRetrievalStatus);
	},
	addDeviceTypeCountsToCurrentServer(state: DevicesState, deviceTypeCounts: DeviceTypeCounts): void {
		Vue.set(state.currentServer, "deviceTypeCounts", deviceTypeCounts);
	},
	addAlarmCountsToCurrentServer(state: DevicesState, alarmCounts: number): void {
		Vue.set(state.currentServer, "alarms", alarmCounts);
	},
	addServer(state: DevicesState, data: ServerDetails): void {
		state.serversList.push(data);
	},
	updateServer(state: DevicesState, server: ServerDetails): void {
		let index = state.serversList.findIndex(s => s.serverID == server.serverID);

		if (index !== -1) {
			Vue.set(state.serversList, index, server);
		}
	},
	setDevices(state: DevicesState, devices: DeviceDetails[]): void {
		state.devices = devices;
	},
	setDevice(state: DevicesState, device: DeviceDetails): void {
		state.device = device;
	},
	appendDevices(state: DevicesState, devices: DeviceDetails[]): void {
		const currentDevices = state.devices.map(x => x.deviceID);
		const newDevices = devices.filter(x => !currentDevices.includes(x.deviceID));

		state.devices = state.devices.concat(newDevices);
	},
	setCurrentDeviceTypeIdentifier(state: DevicesState, deviceTypeIdentifier: DeviceTypeIdentifier): void {
		state.currentDeviceTypeIdentifier = deviceTypeIdentifier;
	},
	setServerTypes(state: DevicesState, serverTypes: ServerType[]): void {
		state.serverTypes = serverTypes;
	},
	addServerTypesNotAlreadyInStore(state: DevicesState, serverTypes: ServerType[]): void {
		serverTypes = serverTypes.filter(
			newServerType => !state.serverTypes.some(existingServerTypes => existingServerTypes.serverTypeID === newServerType.serverTypeID)
		);

		state.serverTypes.push(...serverTypes);
	},
	setServerType(state: DevicesState, serverType: ServerType): void {
		let index = state.serverTypes.findIndex(s => s.serverTypeID == serverType.serverTypeID);

		if (index !== -1) {
			Vue.set(state.serverTypes, index, serverType);
		}
	},
	deleteServer(state: DevicesState, serverId: number): void {
		state.serversList = state.serversList.filter(x => x.serverID !== serverId);
		state.devices = state.devices.filter(x => x.serverID !== serverId);
	},
	deleteDevice(state: DevicesState, deviceId: number): void {
		state.devices = state.devices.filter(x => x.deviceID !== deviceId);
	},
	deleteDevices(state: DevicesState, deviceIds: number[]): void {
		state.devices = state.devices.filter(x => !deviceIds.includes(x.deviceID))
	},
	setDeviceTypeCounts(state: DevicesState, deviceTypeCounts: DeviceTypeCounts): void {
		state.deviceTypeCounts = deviceTypeCounts;
	},
	setAlarmCounts(state: DevicesState, alarmCounts: number): void {
		state.alarms = alarmCounts;
	},
	addDevice(state: DevicesState, device: DeviceDetails): void {
		state.devices.push(device);
	},
	updateDevice(state: DevicesState, device: DeviceDetails): void {
		let index = state.devices.findIndex(d => d.deviceID == device.deviceID);

		if (index !== -1) {
			Vue.set(state.devices, index, device);
		}
	},
	updateDevices(state: DevicesState, devices: DeviceDetails[]): void {
		devices.forEach(function (device) {
			let index = state.devices.findIndex(x => x.deviceID == device.deviceID);

			if (index !== -1) {
				Vue.set(state.devices, index, device);
			}
		});
	},
	addDevicesNotAlreadyInStore(state: DevicesState, devices: DeviceDetails[]): void {
		devices = devices.filter(
			newDevice => !state.devices.some(existingDevice => existingDevice.deviceID === newDevice.deviceID)
		);

		state.devices.push(...devices);
	},
	setDeleteChildDeviceModalVisibility(state: DevicesState, visible: boolean): void {
		state.deleteChildDeviceModalVisible = visible;
	},
	setEditChildDeviceModalVisibility(state: DevicesState, visible: boolean): void {
		state.editChildDeviceModalVisible = visible;
	},
	setCameraPreviewModalVisibility(state: DevicesState, visible: boolean): void {
		state.cameraPreviewModalVisible = visible;
	},
	setNotificationOptions(state: DevicesState, notificationOptions: NotificationOptions | null) {
		state.notificationOptions = notificationOptions;
	},
	setServerLineProfiles(state: DevicesState, serverLineProfiles: ServerLineProfile[]) {
		state.serverLineProfiles = serverLineProfiles;
	},
	setCameraDefaultReferenceShot(state: DevicesState, { DeviceID, Image }: CameraReferenceShot) {
		state.devices.find(d => d.deviceID === DeviceID).defaultReferenceShot = Image;
	},
	resetState(state: DevicesState): void {
		Object.assign(state, getDefaultState());
	}
};
